// src/utils/eventsImportParsing.js
// Pure parsing/matching helpers for the Steward Events Importer (Phase 4).
// No Supabase calls here — this file only turns raw CSV text and file lists
// into structured rows the importer page can review and act on.

import Papa from 'papaparse';

// The 22-column template header, in order — DATABASE.md / docs/events-import/
// ournet-events-import-template.csv is the source of truth for this list.
// `image_filename` was retired in Phase 5c §2 along with the photo picker —
// the capability moved to MyArticles.jsx (per-event, visual, no filename
// matching), which already works better than the picker did.
export const IMPORT_CSV_COLUMNS = [
  'title', 'summary', 'description', 'start_date', 'start_time', 'end_date', 'end_time',
  'all_day', 'locality', 'municipality', 'venue_name', 'venue_address', 'venue_postcode',
  'price_info', 'booking_required', 'max_attendees', 'contact_email', 'contact_phone',
  'event_url', 'image_url', 'tags', 'source'
];

// Row 2 of the distributed template is a specification row, not data — every
// steward CSV that followed the AI-instructions doc will have it in place.
// Detected by a stable substring of its `title` cell rather than an exact
// match, so a steward's minor edit elsewhere in that row doesn't defeat it.
const SPEC_ROW_TITLE_MARKER = 'REQUIRED. THE EVENT NAME';

function isSpecRow(row) {
  return Boolean(row.title && row.title.trim().toUpperCase().startsWith(SPEC_ROW_TITLE_MARKER));
}

function isBlankRow(row) {
  return IMPORT_CSV_COLUMNS.every(col => !row[col] || !String(row[col]).trim());
}

// ---------------------------------------------------------------------------
// Forgiving date parser
// Accepts: ISO (2026-08-14), day-first slash (14/08/2026), written-out
// ("14 August 2026", "14 Aug 26") in English or Portuguese month names.
// Ambiguous slash dates (both parts <=12) resolve day-first per the brief —
// they are NOT flagged. Only genuinely unparseable input is flagged.
// ---------------------------------------------------------------------------
const MONTHS = {
  jan: 1, january: 1, janeiro: 1,
  feb: 2, february: 2, fev: 2, fevereiro: 2,
  mar: 3, march: 3, marco: 3, março: 3,
  apr: 4, april: 4, abr: 4, abril: 4,
  may: 5, maio: 5,
  jun: 6, june: 6, junho: 6,
  jul: 7, july: 7, julho: 7,
  aug: 8, august: 8, ago: 8, agosto: 8,
  sep: 9, sept: 9, september: 9, set: 9, setembro: 9,
  oct: 10, october: 10, out: 10, outubro: 10,
  nov: 11, november: 11, novembro: 11,
  dec: 12, december: 12, dez: 12, dezembro: 12
};

function foldMonthName(word) {
  const key = word.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase().replace(/\.$/, '');
  return MONTHS[key] || null;
}

function normaliseYear(y) {
  const n = parseInt(y, 10);
  if (y.length === 4) return n;
  // 2-digit year: 00-79 -> 2000s, 80-99 -> 1900s (generous window; this pilot only ever needs 2020s-2030s)
  return n <= 79 ? 2000 + n : 1900 + n;
}

function validYmd(year, month, day) {
  if (!year || !month || !day) return null;
  if (month < 1 || month > 12) return null;
  const d = new Date(Date.UTC(year, month - 1, day));
  if (d.getUTCFullYear() !== year || d.getUTCMonth() !== month - 1 || d.getUTCDate() !== day) return null;
  return d;
}

/**
 * Parse a date-only string into a {year, month, day} or null (unparseable).
 */
function parseDateOnly(input) {
  if (!input) return null;
  const s = input.trim();
  if (!s) return null;

  // ISO: YYYY-MM-DD
  let m = s.match(/^(\d{4})-(\d{1,2})-(\d{1,2})$/);
  if (m) {
    const d = validYmd(parseInt(m[1], 10), parseInt(m[2], 10), parseInt(m[3], 10));
    return d ? { date: d } : { error: true };
  }

  // Day-first slash/dot: D/M/YYYY or D/M/YY — always day-first, never guessed the other way
  m = s.match(/^(\d{1,2})[/.](\d{1,2})[/.](\d{2,4})$/);
  if (m) {
    const day = parseInt(m[1], 10);
    const month = parseInt(m[2], 10);
    const year = normaliseYear(m[3]);
    const d = validYmd(year, month, day);
    return d ? { date: d } : { error: true };
  }

  // Written-out: "14 August 2026", "14 Aug 26", "14 de Agosto de 2026"
  m = s.match(/^(\d{1,2})\s+(?:de\s+)?([A-Za-zÀ-ÿ]+)\.?\s+(?:de\s+)?(\d{2,4})$/i);
  if (m) {
    const day = parseInt(m[1], 10);
    const month = foldMonthName(m[2]);
    if (!month) return { error: true };
    const year = normaliseYear(m[3]);
    const d = validYmd(year, month, day);
    return d ? { date: d } : { error: true };
  }

  return { error: true };
}

/**
 * Parse a time-only string into {hours, minutes} or null. Supports "19:30",
 * "7:30 PM", the Portuguese "19h30" / "19h" convention, and "HH:MM:SS" (the
 * seconds are accepted and discarded — this is exactly what a spreadsheet
 * app produces when it silently rewrites a CSV on open, see brief §3.2/§3.5).
 */
function parseTimeOnly(input) {
  if (!input) return null;
  const s = input.trim();
  if (!s) return null;

  let m = s.match(/^(\d{1,2})[:h](\d{2})?(?::\d{2})?\s*(am|pm)?$/i);
  if (m) {
    let hours = parseInt(m[1], 10);
    const minutes = m[2] ? parseInt(m[2], 10) : 0;
    const ampm = m[3]?.toLowerCase();
    if (ampm === 'pm' && hours < 12) hours += 12;
    if (ampm === 'am' && hours === 12) hours = 0;
    if (hours > 23 || minutes > 59) return { error: true };
    return { hours, minutes };
  }
  return { error: true };
}

/**
 * Combine a date field + optional time field into a Date, or a flag.
 * Returns { date: Date } | { date: null, flagged: false } (both blank, ok if optional)
 * | { date: null, flagged: true, reason } (genuinely unparseable).
 */
export function parseFlexibleDateTime(dateInput, timeInput) {
  const dateStr = (dateInput || '').trim();
  const timeStr = (timeInput || '').trim();

  if (!dateStr) {
    return { date: null, flagged: false };
  }

  const parsedDate = parseDateOnly(dateStr);
  if (!parsedDate || parsedDate.error) {
    return { date: null, flagged: true, reasonKey: 'flag_ambiguous_date', reasonParams: { value: dateInput } };
  }

  let hours = 0;
  let minutes = 0;
  if (timeStr) {
    const parsedTime = parseTimeOnly(timeStr);
    if (!parsedTime || parsedTime.error) {
      return { date: null, flagged: true, reasonKey: 'flag_ambiguous_time', reasonParams: { value: timeInput } };
    }
    hours = parsedTime.hours;
    minutes = parsedTime.minutes;
  }

  const d = new Date(parsedDate.date);
  d.setUTCHours(hours, minutes, 0, 0);
  return { date: d, flagged: false };
}

// ---------------------------------------------------------------------------
// yes/no field parsing
// ---------------------------------------------------------------------------
export function parseYesNo(input) {
  const s = (input || '').trim().toLowerCase();
  return s === 'yes' || s === 'y' || s === 'true' || s === 'sim';
}

// ---------------------------------------------------------------------------
// Fuzzy string comparison — small self-contained Levenshtein, no dependency.
// Used for both duplicate-title detection and locality/municipality matching.
// ---------------------------------------------------------------------------
function normaliseForCompare(s) {
  return (s || '')
    .normalize('NFD').replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9\s]/g, '')
    .replace(/\s+/g, ' ')
    .trim();
}

function levenshtein(a, b) {
  const m = a.length, n = b.length;
  if (m === 0) return n;
  if (n === 0) return m;
  const prev = new Array(n + 1);
  const curr = new Array(n + 1);
  for (let j = 0; j <= n; j++) prev[j] = j;
  for (let i = 1; i <= m; i++) {
    curr[0] = i;
    for (let j = 1; j <= n; j++) {
      const cost = a[i - 1] === b[j - 1] ? 0 : 1;
      curr[j] = Math.min(curr[j - 1] + 1, prev[j] + 1, prev[j - 1] + cost);
    }
    for (let j = 0; j <= n; j++) prev[j] = curr[j];
  }
  return prev[n];
}

/**
 * Fuzzy-equal: exact match after normalising, one containing the other, or
 * within a small edit-distance tolerance relative to length.
 */
export function fuzzyTextMatch(a, b) {
  const na = normaliseForCompare(a);
  const nb = normaliseForCompare(b);
  if (!na || !nb) return false;
  if (na === nb) return true;
  if (na.includes(nb) || nb.includes(na)) return true;
  const distance = levenshtein(na, nb);
  const tolerance = Math.ceil(Math.max(na.length, nb.length) * 0.2);
  return distance <= tolerance;
}

// ---------------------------------------------------------------------------
// Tags — the CSV template specifies semicolon-separated tags (docs/events-import/
// ournet-events-import-template.csv). Re-joined on comma, not left semicolon-
// separated, because articles.article_tags is read back by normalizeTags()
// (AddEditEvent.jsx, AddEditArticle.jsx) as JSON or a comma-split string — a
// semicolon-joined string would round-trip as a single tag the moment a
// steward opens the imported event in Edit.
// ---------------------------------------------------------------------------
function parseTagsField(raw) {
  const tags = (raw || '').split(';').map(tag => tag.trim()).filter(Boolean);
  return tags.length > 0 ? tags.join(',') : null;
}

// ---------------------------------------------------------------------------
// Preamble/fence tolerance (Phase 5c §5) — an AI wraps its CSV reply in
// ```/``` fences by reflex despite being told not to, and a steward who
// selects a stray character can lose a whole batch to a parse failure they
// cannot diagnose. This happened live, twice, on 6 August: once the fence
// consumed the header row, producing a wrong-cause rejection ("columns don't
// match"); once a single surviving backtick got silently absorbed into an
// event title, publishing it.
//
// Revised after both failure modes reproduced live against the first version
// of this fix (Michael, same day):
//   - the trailing scan only recognised a fence as the WHOLE last line, so
//     any sign-off text after a closing fence (e.g. "Espero que ajude!")
//     stopped it from trimming anything at all, and the fence line itself
//     plus the sign-off both leaked through as flagged rows — which also
//     made the new row-count guard (§5a) cry wolf on a perfectly good batch.
//   - fence detection was whole-LINE only, so a stray backtick glued to the
//     front of a real data line (not sitting on its own line) was never
//     touched at all — exactly the original incident this section exists
//     to prevent.
// Fixed by: (1) cutting at the LAST closing-fence line found anywhere after
// the header, not only if it is the file's final line — everything from
// that fence onward (fence included) is wrapper, since the fence syntax
// itself marks where the AI considers the code block to end; (2) stripping
// a narrow, unambiguous run of 1-3 leading/trailing backticks (with an
// optional "csv" tag, word-bounded so it can't eat the start of a real word
// like "CSVzao" or "csvida") from every line. This still stops short of
// "repairing a malformed row" — it removes wrapper residue a fence leaves
// at the very start/end of a line, never the middle, and no real event
// field legitimately starts or ends with a backtick.
// ---------------------------------------------------------------------------
const FENCE_ONLY_LINE = /^```[a-zA-Z]*$/;

function stripFenceResidue(line) {
  return line
    .replace(/^`{1,3}(?:csv(?=\s|$))?\s*/i, '')
    .replace(/\s*`{1,3}$/, '');
}

function looksLikeHeaderLine(line) {
  const fields = line.split(',').map(f => f.trim().replace(/^"|"$/g, '').toLowerCase());
  const hits = IMPORT_CSV_COLUMNS.filter(col => fields.includes(col)).length;
  return hits >= Math.ceil(IMPORT_CSV_COLUMNS.length / 2);
}

export function extractCsvBody(rawText) {
  const rawLines = (rawText || '').split(/\r\n|\r|\n/);
  const lines = rawLines.map(stripFenceResidue);

  let start = lines.findIndex(line => line.trim() && looksLikeHeaderLine(line));
  if (start === -1) start = 0; // nothing recognisable as a header — let columnsOk reject it below with a real reason

  // Trailing: find the LAST original (unstripped) fence-only line after the
  // header. Everything from it onward — the fence and anything after —  is
  // wrapper, cut in one go rather than trimmed backward line by line.
  let end = lines.length;
  for (let i = rawLines.length - 1; i > start; i--) {
    if (FENCE_ONLY_LINE.test(rawLines[i].trim())) {
      end = i;
      break;
    }
  }
  if (end === lines.length) {
    // No closing fence found — just trim trailing blank lines.
    while (end > start && !lines[end - 1].trim()) end--;
  }

  return lines.slice(start, end).join('\n');
}

// ---------------------------------------------------------------------------
// CSV parsing entry point
// ---------------------------------------------------------------------------

/**
 * Parse the steward's pasted text into structured rows, tolerating wrapper
 * text (preamble, code fences) around the CSV, skipping the spec row and any
 * blank rows. `rawRowCount` is the parser's own row count BEFORE that
 * filtering — compared against `rows.length` on the review screen (Phase 5c
 * §5a) so a row silently dropped between parse and review is visible rather
 * than invisible.
 * @param {string} csvText
 * @returns {{ rows: Array<object>, columnsOk: boolean, foundColumns: string[], rawRowCount: number }}
 */
export function parseImportCsv(csvText) {
  const cleaned = extractCsvBody(csvText);
  const result = Papa.parse(cleaned, { header: true, skipEmptyLines: true });
  const foundColumns = result.meta?.fields || [];
  const columnsOk = IMPORT_CSV_COLUMNS.every(col => foundColumns.includes(col));
  const rawRowCount = (result.data || []).length;

  const rows = (result.data || [])
    .filter(row => !isSpecRow(row) && !isBlankRow(row))
    .map((row, idx) => buildParsedRow(row, idx));

  return { rows, columnsOk, foundColumns, rawRowCount };
}

function buildParsedRow(row, idx) {
  const flags = [];

  const title = (row.title || '').trim();
  const description = (row.description || '').trim();
  const source = (row.source || '').trim();

  if (!title) flags.push({ type: 'missing_required', field: 'title', messageKey: 'flag_title_required' });
  // description is NOT required (Phase 5b §4, ruled 6 Aug) — a poster photographed with only
  // a title and a date is a usable row; demanding a description means someone invents one.
  // source still blocks — stays the provenance check, never stored.
  if (!source) flags.push({ type: 'missing_required', field: 'source', messageKey: 'flag_source_required' });

  const start = parseFlexibleDateTime(row.start_date, row.start_time);
  if (!row.start_date || !row.start_date.trim()) {
    flags.push({ type: 'missing_required', field: 'start_date', messageKey: 'flag_start_date_required' });
  } else if (start.flagged) {
    flags.push({ type: 'ambiguous_date', field: 'start_date', messageKey: start.reasonKey, messageParams: start.reasonParams });
  }

  const end = parseFlexibleDateTime(
    (row.end_date && row.end_date.trim()) ? row.end_date : (row.end_time && row.end_time.trim() ? row.start_date : ''),
    row.end_time
  );
  if (end.flagged) {
    flags.push({ type: 'ambiguous_date', field: 'end_date', messageKey: end.reasonKey, messageParams: end.reasonParams });
  }

  const maxAttendeesRaw = (row.max_attendees || '').trim();
  const maxAttendees = maxAttendeesRaw && /^\d+$/.test(maxAttendeesRaw) ? parseInt(maxAttendeesRaw, 10) : null;
  if (maxAttendeesRaw && maxAttendees === null) {
    flags.push({ type: 'ambiguous_date', field: 'max_attendees', messageKey: 'flag_max_attendees_invalid', messageParams: { value: maxAttendeesRaw } });
  }

  return {
    rowIndex: idx,
    raw: row,
    title,
    summary: (row.summary || '').trim() || null,
    description,
    startDateTime: start.date,
    endDateTime: end.date,
    isAllDay: parseYesNo(row.all_day),
    localityRaw: (row.locality || '').trim(),
    municipalityRaw: (row.municipality || '').trim(),
    venueName: (row.venue_name || '').trim() || null,
    venueAddress: (row.venue_address || '').trim() || null,
    venuePostcode: (row.venue_postcode || '').trim() || null,
    priceInfo: (row.price_info || '').trim() || null,
    requiresRsvp: parseYesNo(row.booking_required),
    maxAttendees,
    contactEmail: (row.contact_email || '').trim() || null,
    contactPhone: (row.contact_phone || '').trim() || null,
    eventUrl: (row.event_url || '').trim() || null,
    imageUrl: (row.image_url || '').trim() || null,
    tags: parseTagsField(row.tags),
    source,
    flags,
    rightsConfirmed: false,
  };
}

// ---------------------------------------------------------------------------
// Per-row freguesia routing (Phase 5b §2) — REPLACES the old single-freguesia
// mismatch check (checkLocalityMismatch, Phase 4/5a). From this phase,
// locality/municipality are the routing key, not an optional cross-check: a
// row naming a different parish is information about where it belongs, not
// an error. See docs/briefs/2026-08-06-events-importer-phase5b-routing-and-review.md §2.
//
// boundaryNameMatches is deliberately STRICTER than fuzzyTextMatch (exact
// after normalising, no substring/Levenshtein tolerance). fuzzyTextMatch was
// built for duplicate-title comparison, where a loose match is desirable;
// freguesia-name resolution is an authorisation-relevant decision — a wrongly
// routed event is invisible to the people it concerns and visible to people
// it does not (brief §2.3), so this deliberately does not reuse fuzzyTextMatch.
// ---------------------------------------------------------------------------
export function boundaryNameMatches(a, b) {
  const na = normaliseForCompare(a);
  const nb = normaliseForCompare(b);
  return Boolean(na) && na === nb;
}

/**
 * Match a row's locality name against the steward's granted freguesia set
 * (client-side only — the granted set is already loaded, no RPC). Returns
 * the array of matching boundaries: zero (no match in-grant), one (resolved),
 * or more than one (ambiguous within the grant, brief §2.3 rule (d)).
 * @param {string} localityRaw
 * @param {Array<{boundary_id: number, boundary_name: string}>} grantedFreguesias
 */
export function matchFreguesiaWithinGrant(localityRaw, grantedFreguesias) {
  if (!localityRaw) return [];
  return grantedFreguesias.filter(f => boundaryNameMatches(localityRaw, f.boundary_name));
}

// ---------------------------------------------------------------------------
// Duplicate detection against existing (already-imported) events
// (freguesia + start_date + fuzzy title)
// ---------------------------------------------------------------------------
export function findDuplicate(row, existingEvents) {
  if (!row.startDateTime) return null;
  const rowDateKey = row.startDateTime.toISOString().slice(0, 10);
  return existingEvents.find(ev => {
    if (!ev.start_date) return false;
    const evDateKey = new Date(ev.start_date).toISOString().slice(0, 10);
    return evDateKey === rowDateKey && fuzzyTextMatch(row.title, ev.title);
  }) || null;
}

// ---------------------------------------------------------------------------
// Duplicate detection WITHIN the current batch (Phase 5b §6.1) — findDuplicate
// above only ever compares a row against already-imported DB rows; two rows
// with the same title and date inside the SAME parse (a steward re-running
// last month's import, or an AI producing two rows for one event) were never
// flagged. Same matching rule (exact date + fuzzyTextMatch title), applied
// pairwise within the batch. Returns a Set of rowIndex values that share a
// title+date with at least one other row — flagged, not auto-removed, since
// a genuinely recurring event may legitimately repeat.
// ---------------------------------------------------------------------------
export function findDuplicatesWithinBatch(rows) {
  const duplicateRowIndexes = new Set();
  for (let i = 0; i < rows.length; i++) {
    if (!rows[i].startDateTime) continue;
    const dateKeyI = rows[i].startDateTime.toISOString().slice(0, 10);
    for (let j = i + 1; j < rows.length; j++) {
      if (!rows[j].startDateTime) continue;
      const dateKeyJ = rows[j].startDateTime.toISOString().slice(0, 10);
      if (dateKeyI === dateKeyJ && fuzzyTextMatch(rows[i].title, rows[j].title)) {
        duplicateRowIndexes.add(rows[i].rowIndex);
        duplicateRowIndexes.add(rows[j].rowIndex);
      }
    }
  }
  return duplicateRowIndexes;
}
